import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { useNavigate, useOutletContext } from 'react-router'

import { skipToken } from '@reduxjs/toolkit/query'
import { isFuture, isPast } from 'date-fns'
import { cloneDeep, isEmpty, noop } from 'lodash-es'
import styled from 'styled-components/native'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { getDbtTeensSchedulingParticipantNames } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils/dbtTeensScheduling'
import { RECORDING_AVAILABLE_STATUSES } from '@lyrahealth-inc/shared-app-logic/src/features/videoSession/constants'
import { CalendarCardAppointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { APPOINTMENT_STATUS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'
import VideoPlayer from '@lyrahealth-inc/ui-core/lib/atoms/videoPlayer/VideoPlayer'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { PlusSignIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/PlusSignIcon'
import { SessionCompletedCheck } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SessionCompletedCheck'
import { SessionListCancelIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SessionListCancelIcon'
import { SessionListInPersonIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SessionListInPersonIcon'
import { SessionListLiveMessageIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SessionListLiveMessageIcon'
import { SessionListPhoneIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SessionListPhoneIcon'
import { VideoIconWithEllipse } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/VideoIconWithEllipse'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { useMediaQuerySize } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useMediaQuerySize'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { SessionList } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/sessionList/SessionList'
import { colors } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/colors'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import ProgramAwareSessionsView from './programAware/ProgramAwareSessionsView'
import { actions, mixpanelEvents } from '../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../mixpanel/mixpanelTracking'
import { cloudfrontOriginUrl, episodeStates, programConfig } from '../../common/constants/appConstants'
import {
  CLIENTS_CANCEL_SESSION,
  CLIENTS_NEW_SESSION,
  CLIENTS_RESCHEDULE_SESSION_DETAILS,
} from '../../common/constants/routingConstants'
import { getAuthUserId } from '../../data/auth/authSelectors'
import { getSelectedClientId } from '../../data/lyraTherapy/clientSelectors'
import { useAppDispatch } from '../../data/store'
import { useIsEngageDAProvider } from '../../hooks/useIsEngageDAProvider'
import SessionsPDF from '../clients/clientDetails/clientRecords/SessionsPDF'
import { clearSelectedAppointment, setAppointment } from '../clients/clientDetails/data/appointmentsSlice'
import { useClientAppointmentsData } from '../clients/clientDetails/data/hooks/useClientAppointmentsData'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { useGetVideoSessionsQuery, useLazyGetVideoSessionRecordingQuery } from '../data/videoSessionApi'
import { useSelectedEpisodeForClient } from '../episodes/data/hooks/useSelectedEpisodeForClient'

const ScheduleSessionContainer = styled.View({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
})

const EmptyState = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: 80,
  borderWidth: 1,
  borderStyle: 'solid',
  borderRadius: 16,
  borderColor: colors.ui_oatmeal3,
  backgroundColor: colors.white,
  fontSize: '16px',
  paddingLeft: theme.spacing['16px'],
  paddingRight: theme.spacing['16px'],
  textAlign: 'center',
}))

const LoadingContainer = styled.View({
  textAlign: 'center',
  margin: '30px 0',
})

interface SessionPageWrapperProps {
  testID?: undefined | string
}

const SessionPageWrapper = styled.View.attrs<SessionPageWrapperProps>((props) => ({
  'data-testid': props.testID,
}))<SessionPageWrapperProps>({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
})

const SessionContainer = styled.View(({ isMinWidthTablet }: { isMinWidthTablet: boolean }) => ({
  width: isMinWidthTablet ? '544px' : '100%',
}))

/**
 * Displays a list of sessions between the authenticated provider and a given client.
 * (A session is an object consisting of an appointment and some amount of associated curriculum
 * for the client to complete. Session numbering is relative to chronological order of appointments.)
 * Allows the provider to create new appointments as well as both reschedule and cancel
 * existing appointments.
 */
const Sessions: React.FC<SessionsProps> = ({
  setSessionsPDFContent,
  providerEpisodesInfoPDF,
  pdfContentStartDate,
  pdfContentEndDate,
  withProgramAwareSessionsExperience = false,
}) => {
  const dispatch = useAppDispatch()
  const { setShowSubNav } = useOutletContext<{ setShowSubNav: (visibility: boolean) => void }>()
  const id = useSelector(getAuthUserId)
  const { data: clientDetails } = useSelectedClientData()
  const clientId = useSelector(getSelectedClientId)
  const [getVideoSessionRecording] = useLazyGetVideoSessionRecordingQuery()

  const { data: selectedEpisode } = useSelectedEpisodeForClient() as { data: Episode | undefined }
  const selectedEpisodeProgramConfig = selectedEpisode && programConfig[selectedEpisode.program_name]
  const shouldUseProgramAwareView =
    withProgramAwareSessionsExperience && selectedEpisode?.program_name === ProgramNames.DialecticalBehaviorTherapyTeens
  const canScheduleSession =
    selectedEpisode?.state === episodeStates.IN_PROGRESS &&
    !selectedEpisodeProgramConfig?.hideSessionsPageScheduleButton

  const [modalContent, setModalContent] = useState<JSX.Element | null>(null)
  const [isPlayingVideo, setIsPlayingVideo] = useState<boolean>(false)
  const [isPDFContentInitialized, setIsPDFContentInitialized] = React.useState(false)
  const videoRef = useRef(null)
  const navigate = useNavigate()
  const {
    data: videoSessions,
    isSuccess: hasFetched,
    isLoading,
  } = useGetVideoSessionsQuery(clientId ? { providerId: id, patientId: clientId } : skipToken, {
    refetchOnMountOrArgChange: true,
  })
  const { data: allAppointments, isFetching: isRefreshingSessions } = useClientAppointmentsData()
  const isDAEngageProvider = useIsEngageDAProvider()
  const appointments = useMemo(() => {
    if (
      videoSessions == null ||
      videoSessions.length === 0 ||
      allAppointments == null ||
      allAppointments.length === 0
    ) {
      return (allAppointments ?? []) as CalendarCardAppointment[]
    }
    return allAppointments.map<CalendarCardAppointment>((appt) => {
      const videoSession = videoSessions.find(
        (session) => parseInt(session.appointment_id, 10) === parseInt(appt.appointmentId as any, 10),
      )
      if (!videoSession || !RECORDING_AVAILABLE_STATUSES.has(videoSession.recording_status)) return appt
      const apptCopy: CalendarCardAppointment = cloneDeep(appt)
      apptCopy.videoRecording = videoSession.id
      return apptCopy
    })
  }, [allAppointments, videoSessions])

  const goToCreateNewSession = () => {
    track({
      event: mixpanelEvents.SCHEDULE_NEW_SESSION,
    })
    dispatch(clearSelectedAppointment())
    navigate(CLIENTS_NEW_SESSION.route)
  }

  /**
   * Handler for when a provider selects an appointment to be cancelled. This triggers a
   * cancellation modal and does not cover the actual appointment cancellation.
   */
  const selectAppointmentToCancel = (appointment: CalendarCardAppointment) => {
    if (!clientDetails || !appointment || !appointment.appointmentId) {
      return
    }

    if (isDAEngageProvider) {
      track({
        event: mixpanelEvents.CANCEL_SESSION,
        details: {
          appointmentId: appointment.appointmentId,
          entryPoint: 'session',
        },
      })
    } else {
      track({
        event: mixpanelEvents.BUTTON_PRESS,
        action: actions.CANCEL_SESSION,
        details: {
          appointmentId: appointment.appointmentId,
        },
      })
    }

    dispatch(setAppointment(appointment))
    navigate(CLIENTS_CANCEL_SESSION.route)
  }

  /**
   * Handler for when a provider selects an appointment to be rescheduled.
   */
  const rescheduleAppointment = (appointment: CalendarCardAppointment) => {
    if (isDAEngageProvider) {
      track({
        event: mixpanelEvents.RESCHEDULE_SESSION,
        details: {
          appointmentId: appointment.appointmentId,
          entryPoint: 'session',
        },
      })
    } else {
      track({
        event: mixpanelEvents.BUTTON_PRESS,
        action: actions.RESCHEDULE_SESSION,
        details: {
          appointmentId: appointment.appointmentId,
        },
      })
    }

    dispatch(setAppointment(appointment))
    navigate(CLIENTS_RESCHEDULE_SESSION_DETAILS.route)
  }

  const getProviderIcon = (meetingFormat = '', isInPast = false) => {
    let icon
    if (isInPast) {
      return meetingFormat === 'canceled' || meetingFormat === 'cancelled' ? (
        <SessionListCancelIcon />
      ) : (
        <SessionCompletedCheck size={40} />
      )
    }
    switch (meetingFormat) {
      case 'video':
        icon = <VideoIconWithEllipse size={40} backgroundColor={colors.ui_oatmeal2} fillColor={colors.ui_oatmeal5} />
        break
      case 'inPerson':
        icon = <SessionListInPersonIcon size={40} />
        break
      case 'live_messaging':
        icon = <SessionListLiveMessageIcon size={40} />
        break
      case 'canceled':
      case 'cancelled':
        icon = <SessionListCancelIcon />
        break
      default:
        icon = <SessionListPhoneIcon size={40} />
    }
    return icon
  }

  const viewSessionRecording = (sessionID: string) => {
    track({ event: mixpanelEvents.BUTTON_PRESS, action: 'WATCH_RECORDING' })
    getVideoSessionRecording({ id: sessionID })
      .unwrap()
      .then((recording) => {
        if (recording.url) {
          setIsPlayingVideo(true)
          setModalContent(
            <VideoPlayer
              url={recording?.url}
              reference={videoRef}
              onError={(e: Error) => console.log('onError', e)}
              height='100%'
              width='100%'
              style={{ position: 'absolute', left: 0, top: 0, paddingTop: '40px' }}
              controls={true}
              config={{
                file: {
                  hlsOptions: {
                    xhrSetup: (xhr: XMLHttpRequest) => {
                      xhr.withCredentials = true // send cookies
                      xhr.setRequestHeader('Access-Control-Allow-Headers', 'Content-Type, Accept, X-Requested-With')
                      xhr.setRequestHeader('Access-Control-Allow-Origin', cloudfrontOriginUrl)
                      xhr.setRequestHeader('Access-Control-Allow-Credentials', 'true')
                    },
                  },
                },
              }}
              playing
            />,
          )
        }
      })
  }
  const getMeetingFormatAndDuration = (format: string, duration: number): ReactElement => {
    if (format) {
      const lowered = format.toLowerCase()
      if (lowered.indexOf('person') !== -1) {
        return (
          <FormattedMessage
            defaultMessage='In person{spacer}{duration}{minutesAbbreviation}'
            description='Format of the session will be in person instead of on video or phone'
            values={{ spacer: ' • ', duration: duration, minutesAbbreviation: 'min' }}
          />
        )
      } else if (lowered.indexOf('phone') !== -1) {
        return (
          <FormattedMessage
            defaultMessage='Phone{spacer}{duration}{minutesAbbreviation}'
            description='Format of the session will be via telephone or phone'
            values={{ spacer: ' • ', duration: duration, minutesAbbreviation: 'min' }}
          />
        )
      } else if (lowered.indexOf('video') !== -1) {
        return (
          <FormattedMessage
            defaultMessage='Video{spacer}{duration}{minutesAbbreviation}'
            description='Format of the session will be a video call'
            values={{ spacer: ' • ', duration: duration, minutesAbbreviation: 'min' }}
          />
        )
      } else if (lowered.indexOf('message') !== -1 || lowered.indexOf('messaging') !== -1) {
        return (
          <FormattedMessage
            defaultMessage='Live messaging{spacer}{duration}{minutesAbbreviation}'
            description='Format of the session will be via live typed messages'
            values={{ spacer: ' • ', duration: duration, minutesAbbreviation: 'min' }}
          />
        )
      }
    }
    return <></>
  }
  const getTitle = (appointment: CalendarCardAppointment): string => {
    switch (appointment.appointmentStatus) {
      case APPOINTMENT_STATUS.MISSED:
        return 'Missed Session'
      case APPOINTMENT_STATUS.CANCELED:
        return 'Canceled Session'
      default:
        return `Session ${appointment?.sessionNumber}`
    }
  }

  const selectedEpisodeId = selectedEpisode?.id
  const appointmentsForSelectedEpisode = useMemo(
    () => appointments.filter((appointment) => appointment.episodeId === selectedEpisodeId),
    [appointments, selectedEpisodeId],
  )

  const pastAppointments = appointmentsForSelectedEpisode
    .filter((appointment: CalendarCardAppointment) => {
      const sessionStart = getDateFromAppointment(appointment)
      return isPast(sessionStart) || appointment.appointmentStatus === 'canceled'
    })
    .map((appt) => {
      const addedSubtitle = {
        ...appt,
        subtitle: getMeetingFormatAndDuration(appt?.meetingFormat, appt?.appointmentDuration),
      }
      return addedSubtitle
    })
  const currentAppointments = appointmentsForSelectedEpisode
    .filter((appointment: CalendarCardAppointment) => {
      const sessionStart = getDateFromAppointment(appointment)
      return isFuture(sessionStart) && appointment.appointmentStatus !== 'canceled'
    })
    .map((appt) => {
      const addedSubtitle = {
        ...appt,
        subtitle: getMeetingFormatAndDuration(appt?.meetingFormat, appt?.appointmentDuration),
      }
      return addedSubtitle
    })
  const { caregiverName, teenName } = getDbtTeensSchedulingParticipantNames({
    appointments,
    client: clientDetails,
    episode: selectedEpisode,
  })

  const { isMinWidthTablet } = useMediaQuerySize()

  useEffect(() => {
    setShowSubNav(true)
  }, [setShowSubNav])

  useEffect(() => {
    setIsPDFContentInitialized(false)
  }, [pdfContentStartDate, pdfContentEndDate])

  useEffect(() => {
    if (!setSessionsPDFContent || isPDFContentInitialized) {
      return
    }
    setIsPDFContentInitialized(true)
    setSessionsPDFContent(
      SessionsPDF({ pastAppointments, providerEpisodesInfoPDF, pdfContentStartDate, pdfContentEndDate }),
    )
  }, [
    currentAppointments,
    pastAppointments,
    setSessionsPDFContent,
    pdfContentStartDate,
    pdfContentEndDate,
    isPDFContentInitialized,
    providerEpisodesInfoPDF,
  ])

  return (
    <SessionPageWrapper testID={tID('ClientSessions-tab')}>
      {(isEmpty(appointmentsForSelectedEpisode) && (isLoading || !hasFetched)) ||
      isRefreshingSessions ||
      !clientDetails ? (
        <LoadingContainer>
          <LoadingIndicator size={45} />
        </LoadingContainer>
      ) : (
        <SessionContainer isMinWidthTablet={isMinWidthTablet && !shouldUseProgramAwareView}>
          {shouldUseProgramAwareView ? (
            <ProgramAwareSessionsView
              authProviderId={id}
              canScheduleSession={canScheduleSession}
              caregiverFirstName={caregiverName}
              clientFirstName={teenName}
              onCancelSession={selectAppointmentToCancel}
              onRescheduleSession={rescheduleAppointment}
              onScheduleSession={goToCreateNewSession}
              onViewSessionRecording={viewSessionRecording}
              pastAppointments={pastAppointments as unknown as CalendarCardAppointment[]}
              upcomingAppointments={currentAppointments as unknown as CalendarCardAppointment[]}
            />
          ) : (
            <>
              {canScheduleSession && (
                <ScheduleSessionContainer>
                  <PrimaryButton
                    text='Schedule session'
                    onPress={goToCreateNewSession}
                    testID={tID('Sessions-new-session')}
                    leftIcon={<PlusSignIcon size={16} />}
                  ></PrimaryButton>
                </ScheduleSessionContainer>
              )}
              <SessionList
                title={
                  <FormattedMessage
                    defaultMessage='Upcoming sessions'
                    description='Title for sessions coming up in the future'
                  />
                }
                sessions={currentAppointments as unknown as CalendarCardAppointment[]}
                handleReschedulePress={rescheduleAppointment}
                handleCancelPress={selectAppointmentToCancel}
                getProviderIcon={getProviderIcon}
                testId={tID('Upcoming')}
                emptyListMessage={
                  isDAEngageProvider ? (
                    <EmptyState>
                      <FormattedMessage
                        defaultMessage='No upcoming sessions. Upcoming sessions must be scheduled through the calendar tab.'
                        description='Message displayed for when no future sessions are scheduled'
                      />
                    </EmptyState>
                  ) : (
                    <EmptyState>
                      <FormattedMessage
                        defaultMessage='No upcoming sessions.'
                        description='Message displayed for when no future sessions are scheduled'
                      />
                    </EmptyState>
                  )
                }
                getCardTitle={getTitle}
                onPressContactCareNavigator={noop}
                isFullHeight
              />
              {pastAppointments.length > 0 && (
                <SessionList
                  title={
                    <FormattedMessage
                      defaultMessage='Past sessions'
                      description='Title for sessions that happened in the past'
                    />
                  }
                  sessions={pastAppointments as unknown as CalendarCardAppointment[]}
                  getProviderIcon={getProviderIcon}
                  viewSessionRecording={viewSessionRecording}
                  testId={tID('Past')}
                  getCardTitle={getTitle}
                  onPressContactCareNavigator={noop}
                  isDAEngageProvider={isDAEngageProvider}
                  isFullHeight
                />
              )}
            </>
          )}

          <Modal
            visible={!!modalContent}
            modalContents={modalContent ?? <div />}
            scrollableModalWidth={isPlayingVideo ? '900px' : undefined}
            scrollableModalHeight={isPlayingVideo ? '530px' : 'auto'}
            onCloseEnd={() => {
              setModalContent(null)
              setIsPlayingVideo(false)
            }}
            onRequestClose={() => {
              setModalContent(null)
              setIsPlayingVideo(false)
            }}
            scrollable
            isHeaderTransparent
            disableBottomSheet
          />
        </SessionContainer>
      )}
    </SessionPageWrapper>
  )
}

type SessionsProps = {
  setSessionsPDFContent?: (content: { [key: string]: React.ReactNode | undefined } | null) => void
  providerEpisodesInfoPDF?: { text: string; id: string }[]
  pdfContentStartDate?: string
  pdfContentEndDate?: string
  withProgramAwareSessionsExperience?: boolean
}

export default Sessions
